import { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Select, Textarea, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Channel } from "../lib/api.js";
import "./new-channel-dialog.css";

/**
 * Creating a channel.
 *
 * A dialog rather than an inline affordance because a channel needs more than
 * a name to be useful — a topic so people know what belongs in it, and the
 * project it belongs to. Direct conversations deliberately do not come
 * through here: they need only a person, so they are started by searching the
 * sidebar, the way messaging apps do it.
 */

type ProjectOption = { id: string; code: string; name: string };

export interface NewChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (channel: Channel) => void;
  /** Pre-selects a project when raised from that project's surface. */
  projectId?: string | null;
}

export function NewChannelDialog({ open, onClose, onCreated, projectId }: NewChannelDialogProps) {
  const { toast } = useToast();

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [chosenProject, setChosenProject] = useState(projectId ?? "");
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setTopic("");
    setChosenProject(projectId ?? "");
    setIsPrivate(false);
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get<{ projects: ProjectOption[] }>("/projects")
      .then((res) => !cancelled && setProjects(res.projects))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    api
      .post<{ channel: Channel }>("/channels", {
        name: trimmed,
        topic: topic.trim() || undefined,
        // Attached to a project makes it a project channel; otherwise it is
        // team-wide. This is the same split the sidebar groups by.
        kind: chosenProject ? "project" : "team",
        projectId: chosenProject || undefined,
        isPrivate,
      })
      .then((res) => {
        onCreated(res.channel);
        onClose();
      })
      .catch((error: unknown) => {
        toast(error instanceof ApiError ? error.message : "Could not create that channel.", { tone: "danger" });
      })
      .finally(() => setCreating(false));
  }, [name, topic, chosenProject, isPrivate, onCreated, onClose, toast]);

  return (
    <Modal open={open} onClose={onClose} title="New channel">
      <form
        className="new-channel"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          label="Channel name"
          placeholder="cutover-planning"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
        />

        <Textarea
          label="Topic"
          hint="One line on what belongs here. Shown under the channel name."
          placeholder="Coordination for the August cutover weekend."
          value={topic}
          maxRows={3}
          onChange={(e) => setTopic(e.target.value)}
        />

        {!projectId && (
          <Select label="Project" value={chosenProject} onChange={(e) => setChosenProject(e.target.value)}>
            <option value="">No project — a team-wide channel</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.code} · {project.name}
              </option>
            ))}
          </Select>
        )}

        <label className="new-channel__private">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
          <span>
            <strong>Private</strong>
            <span>Only people added to it can find or read this channel.</span>
          </span>
        </label>

        <div className="new-channel__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={creating} disabled={!name.trim() || creating}>
            Create channel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
